import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  getDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { createNotification } from './notificationService';

/**
 * Follow a user
 */
export const followUser = async (
  followerId: string,
  followingId: string,
  followerName: string
) => {
  try {
    const followData = {
      followerId,
      followingId,
      createdAt: Timestamp.now(),
    };

    await setDoc(
      doc(db, 'follows', `${followerId}_${followingId}`),
      followData
    );

    // Notify the user being followed (WITHOUT imageUrl)
    await createNotification(
      followingId,
      'follow',
      'New Follower',
      `${followerName} started following you`,
      undefined, // imageUrl intentionally omitted
      `/profile/${followerId}`
    );
  } catch (error) {
    console.error('Error following user:', error);
    throw error;
  }
};

/**
 * Unfollow a user
 */
export const unfollowUser = async (
  followerId: string,
  followingId: string
) => {
  const followRef = doc(db, 'follows', `${followerId}_${followingId}`);
  await deleteDoc(followRef);
};

/**
 * Check if a user is following another user
 */
export const isFollowing = async (
  followerId: string,
  followingId: string
): Promise<boolean> => {
  const followRef = doc(db, 'follows', `${followerId}_${followingId}`);
  const followDoc = await getDoc(followRef);
  return followDoc.exists();
};

/**
 * Get followers count
 */
export const getFollowersCount = async (
  userId: string
): Promise<number> => {
  const q = query(
    collection(db, 'follows'),
    where('followingId', '==', userId)
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.size;
};

/**
 * Get following count
 */
export const getFollowingCount = async (
  userId: string
): Promise<number> => {
  const q = query(
    collection(db, 'follows'),
    where('followerId', '==', userId)
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.size;
};

/**
 * Get list of follower IDs
 */
export const getFollowers = async (
  userId: string
): Promise<string[]> => {
  const q = query(
    collection(db, 'follows'),
    where('followingId', '==', userId)
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => doc.data().followerId);
};

/**
 * Get list of following IDs
 */
export const getFollowing = async (
  userId: string
): Promise<string[]> => {
  const q = query(
    collection(db, 'follows'),
    where('followerId', '==', userId)
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => doc.data().followingId);
};
